import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Text,
  Button,
  Avatar,
  Spinner,
  Slider,
  makeStyles,
  shorthands,
  tokens,
} from '@fluentui/react-components';
import {
  Dismiss24Regular,
  Call24Regular,
  Chat24Regular,
  VehicleCab24Regular,
  Location24Regular,
  Clock24Regular,
  Share24Regular,
} from '@fluentui/react-icons';
import { TrackingInfo } from '../types';
import { getMapConnector, getActiveMapsProvider } from '../../../../shared/maps';
import type { MapHandle } from '../../../../shared/maps';

// Phase 4 (#325) migration: was importing `mapbox-gl` directly. Now goes
// through the active connector so flipping `VITE_MAPS_PROVIDER` between
// `google` and `mapbox` swaps providers without code change.
//
// NOTE on route polyline: This page used to fetch a multi-point driving
// route from the Mapbox Directions API and render it as a `LineString`
// layer. We still call the Mapbox Directions API (the only provider with a
// public route endpoint we're using today) to sample route points along the
// driving path, and then hand the sampled points to the connector's
// generic `<Polyline>` component for rendering. The polyline draws straight
// segments between consecutive points — fine here because we sample the
// Mapbox-resolved route at high density. Replacing the Directions API
// dependency with Google's Routes API is a future ticket (#321 follow-up).
//
// Debug-mode-only visualizations dropped during the migration:
//  - Numbered debug point markers along the route (Mapbox `circle` +
//    `symbol` layers). Geofence detection JS is preserved (driver-in-circle
//    haversine check still works); only the visual layer is gone. Re-add
//    via a `Circle` capability on MapConnector when needed.
//  - Geofence circle overlays (Mapbox `fill` + `line` layers). Same as
//    above — JS detection still runs, just the visual is gone.
//  - NavigationControl (provider-default UI; Google's default UI fills the
//    role on the Google path; Mapbox runs without it).
const { MapView, Marker, Polyline } = getMapConnector();
const ACTIVE_PROVIDER = getActiveMapsProvider();

const useStyles = makeStyles({
  container: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: tokens.colorNeutralBackground1,
    zIndex: 1000,
    display: 'flex',
    flexDirection: 'column',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    ...shorthands.padding('12px', '16px'),
    ...shorthands.borderBottom('1px', 'solid', tokens.colorNeutralStroke2),
    backgroundColor: tokens.colorNeutralBackground1,
  },
  headerTitle: {
    fontSize: '18px',
    fontWeight: '600',
    color: tokens.colorNeutralForeground1,
  },
  headerActions: {
    display: 'flex',
    gap: '8px',
  },
  mapContainer: {
    flex: 1,
    position: 'relative',
  },
  map: {
    width: '100%',
    height: '100%',
  },
  infoPanel: {
    position: 'absolute',
    bottom: '24px',
    left: '24px',
    right: '24px',
    backgroundColor: tokens.colorNeutralBackground1,
    ...shorthands.borderRadius('16px'),
    boxShadow: tokens.shadow16,
    ...shorthands.overflow('hidden'),
    maxWidth: '600px',
    '@media (min-width: 768px)': {
      left: 'auto',
      right: '24px',
      width: '400px',
    },
  },
  etaSection: {
    ...shorthands.padding('20px'),
    background: 'linear-gradient(135deg, #0078d4 0%, #004578 100%)',
    color: '#ffffff',
  },
  etaHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    marginBottom: '8px',
  },
  etaIcon: {
    width: '40px',
    height: '40px',
    ...shorthands.borderRadius('50%'),
    backgroundColor: 'rgba(255, 255, 255, 0.2)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  etaTitle: {
    fontSize: '14px',
    opacity: 0.9,
  },
  etaValue: {
    fontSize: '32px',
    fontWeight: '600',
  },
  etaSubtitle: {
    fontSize: '14px',
    opacity: 0.8,
    marginTop: '4px',
  },
  refreshControl: {
    position: 'absolute',
    top: '80px',
    left: '12px',
    backgroundColor: tokens.colorNeutralBackground1,
    ...shorthands.borderRadius('8px'),
    boxShadow: tokens.shadow8,
    ...shorthands.padding('12px'),
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    minWidth: '160px',
  },
  refreshLabel: {
    fontSize: '12px',
    fontWeight: '600',
    color: tokens.colorNeutralForeground2,
  },
  refreshSlider: {
    width: '100%',
  },
  refreshValue: {
    fontSize: '12px',
    color: tokens.colorNeutralForeground3,
    textAlign: 'center' as const,
  },
  controlDivider: {
    height: '1px',
    backgroundColor: tokens.colorNeutralStroke2,
    ...shorthands.margin('4px', '0'),
  },
  debugOverlay: {
    position: 'absolute',
    top: '80px',
    right: '12px',
    backgroundColor: 'rgba(0, 0, 0, 0.85)',
    color: '#00ff00',
    ...shorthands.borderRadius('8px'),
    ...shorthands.padding('12px', '16px'),
    fontFamily: 'monospace',
    fontSize: '12px',
    minWidth: '250px',
    zIndex: 1000,
  },
  debugTitle: {
    fontSize: '14px',
    fontWeight: '600',
    marginBottom: '8px',
    color: '#00ff00',
    borderBottom: '1px solid #00ff00',
    paddingBottom: '4px',
  },
  debugRow: {
    display: 'flex',
    justifyContent: 'space-between',
    marginBottom: '4px',
  },
  debugLabel: {
    color: '#88ff88',
  },
  debugValue: {
    color: '#00ff00',
    fontWeight: '600',
  },
  driverSection: {
    ...shorthands.padding('16px', '20px'),
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
    ...shorthands.borderBottom('1px', 'solid', tokens.colorNeutralStroke2),
  },
  driverInfo: {
    flex: 1,
  },
  driverName: {
    fontSize: '16px',
    fontWeight: '600',
    color: tokens.colorNeutralForeground1,
  },
  driverVehicle: {
    fontSize: '14px',
    color: tokens.colorNeutralForeground2,
  },
  driverActions: {
    display: 'flex',
    gap: '8px',
  },
  destinationSection: {
    ...shorthands.padding('16px', '20px'),
  },
  destinationLabel: {
    fontSize: '12px',
    fontWeight: '600',
    color: tokens.colorNeutralForeground3,
    textTransform: 'uppercase',
    marginBottom: '8px',
  },
  destinationAddress: {
    fontSize: '14px',
    color: tokens.colorNeutralForeground1,
    display: 'flex',
    alignItems: 'flex-start',
    gap: '8px',
  },
  loading: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100vh',
  },
  waitingOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.7)',
    backdropFilter: 'blur(8px)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    ...shorthands.padding('48px'),
    zIndex: 100,
  },
  waitingContent: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    maxWidth: '500px',
    textAlign: 'center' as const,
  },
  waitingTitle: {
    fontSize: '28px',
    fontWeight: '600',
    color: '#ffffff',
    marginBottom: '8px',
  },
  waitingSubtitle: {
    fontSize: '16px',
    color: '#e0e0e0',
    marginBottom: '32px',
  },
  etaCard: {
    backgroundColor: tokens.colorNeutralBackground2,
    ...shorthands.borderRadius('12px'),
    ...shorthands.padding('24px'),
    marginBottom: '24px',
    width: '100%',
    boxShadow: tokens.shadow4,
  },
  etaCardLabel: {
    fontSize: '12px',
    fontWeight: '600',
    color: tokens.colorNeutralForeground3,
    textTransform: 'uppercase',
    marginBottom: '8px',
  },
  etaCardValue: {
    fontSize: '36px',
    fontWeight: '600',
    color: tokens.colorBrandForeground1,
    marginBottom: '4px',
  },
  etaCardTime: {
    fontSize: '16px',
    color: tokens.colorNeutralForeground2,
  },
  waitingNote: {
    fontSize: '14px',
    color: '#b0b0b0',
    fontStyle: 'italic',
  },
  pulsingDot: {
    width: '16px',
    height: '16px',
    ...shorthands.borderRadius('50%'),
    backgroundColor: '#ff8c00',
    boxShadow: '0 0 0 4px rgba(255, 140, 0, 0.3)',
    animation: 'pulse 2s infinite',
  },
});

// Mock tracking data
const destinationCoords = { lat: 18.46713535465685, lng: -66.1052214696985 }; // Near Old San Juan
const destinationAddress = 'Anexo del Senado - San Juan, 00901 ';
const startCoords = { lat: 18.465498537837973, lng: -66.1000063427471 }; // Starting point

// Mock geofences - positioned along the route
// For circles, we store center point and radius (in meters)
const geofences = [
  {
    id: 'customer-zone',
    name: 'Customer Delivery Zone',
    type: 'circle' as const,
    center: [-66.1050, 18.4675], // [lng, lat]
    radius: 100, // meters
    color: '#00ff00',
  },
  {
    id: 'midpoint-zone',
    name: 'Midpoint Check Zone',
    type: 'circle' as const,
    center: [-66.10215044753845, 18.467158209695324],
    radius: 80,
    color: '#ffaa00',
  },
  {
    id: 'starting-zone',
    name: 'Starting Area',
    type: 'circle' as const,
    center: [-66.1000, 18.4656],
    radius: 75,
    color: '#0088ff',
  },
];

const getMockTracking = (orderId: string): TrackingInfo => ({
  orderId,
  status: 'en_route',
  etaMinutes: 0, // Will be calculated dynamically based on route
  driverLocation: {
    lat: startCoords.lat,
    lng: startCoords.lng,
    heading: 45,
    speed: 35,
    lastUpdated: new Date().toISOString(),
  },
  driver: {
    id: 'driver-1',
    name: 'José García',
    truckId: 'truck-102',
    truckName: 'Truck 102',
  },
  timeline: [],
});

const LiveTracking: React.FC = () => {
  const styles = useStyles();
  const { orderId } = useParams<{ orderId: string }>();
  const navigate = useNavigate();
  // Connector handle — driver flyTo, fitBounds. The MapView/Marker/Polyline
  // are rendered declaratively below; we don't hold direct refs to provider
  // instances anymore.
  const mapHandleRef = useRef<MapHandle | null>(null);
  const [mapReady, setMapReady] = useState(false);
  const fittedBoundsRef = useRef(false);
  const currentRouteIndexRef = useRef(0);
  const routeProgressRef = useRef(0); // Fractional progress through route (0 to routePoints.length-1)
  const [loading, setLoading] = useState(true);
  const [tracking, setTracking] = useState<TrackingInfo | null>(null);
  const [lastUpdated, setLastUpdated] = useState<Date>(new Date());
  const [routePoints, setRoutePoints] = useState<Array<{ lat: number; lng: number }>>([]);
  const [refreshInterval, setRefreshInterval] = useState(1); // seconds
  const [driverSpeed, setDriverSpeed] = useState(0.30); // movement speed multiplier (0.05 to 0.30) - set to fast for demo
  const [debugMode, setDebugMode] = useState(false);
  const [mapZoom, setMapZoom] = useState(18);
  const [activeGeofences, setActiveGeofences] = useState<string[]>([]); // IDs of geofences truck is currently in

  // Simulated driver movement along route points
  const simulateDriverMovement = useCallback(() => {
    if (!tracking?.driverLocation || routePoints.length === 0) return;

    // Advance progress along route
    // Speed ranges from 0.05 (slow) to 0.30 (fast)
    // Convert to points per update with smaller increments for smoothness
    const progressIncrement = driverSpeed * 10; // 0.5 to 3.0 points per update
    routeProgressRef.current = Math.min(routeProgressRef.current + progressIncrement, routePoints.length - 1);

    // Check if we've reached the destination
    if (routeProgressRef.current >= routePoints.length - 1) {
      setTracking(prev => prev ? {
        ...prev,
        etaMinutes: 0,
        status: 'arrived',
      } : null);
      return;
    }

    // Get integer index and fractional part for interpolation
    const currentIndex = Math.floor(routeProgressRef.current);
    const fraction = routeProgressRef.current - currentIndex;
    currentRouteIndexRef.current = currentIndex;

    // Get current and next points for interpolation
    const currentPoint = routePoints[currentIndex];
    const nextPoint = routePoints[Math.min(currentIndex + 1, routePoints.length - 1)];

    // Interpolate position between current and next point for smooth movement
    const interpolatedLat = currentPoint.lat + (nextPoint.lat - currentPoint.lat) * fraction;
    const interpolatedLng = currentPoint.lng + (nextPoint.lng - currentPoint.lng) * fraction;

    // Calculate ETA based on remaining route points and current speed
    const remainingPoints = routePoints.length - routeProgressRef.current;
    const pointsPerSecond = progressIncrement / refreshInterval;
    const secondsRemaining = remainingPoints / pointsPerSecond;
    const newEta = Math.max(0, Math.round(secondsRemaining / 60)); // Convert to minutes

    setTracking(prev => prev ? {
      ...prev,
      etaMinutes: newEta,
      driverLocation: {
        ...prev.driverLocation!,
        lat: interpolatedLat,
        lng: interpolatedLng,
        lastUpdated: new Date().toISOString(),
      },
    } : null);

    setLastUpdated(new Date());
  }, [tracking?.driverLocation, routePoints, driverSpeed]);

  // Fetch route from Mapbox Directions API. We still hit Mapbox here even
  // when MAPS_PROVIDER=google — Google's Routes API integration is a
  // future ticket (#321 follow-up). The resolved points are passed to the
  // connector's generic `<Polyline>` for rendering, so swapping providers
  // for the visual is decoupled from the route-source vendor.
  const mapboxDirectionsToken = (import.meta as any).env?.VITE_MAPBOX_API_KEY || '';
  const fetchRoute = async () => {
    try {
      if (!mapboxDirectionsToken) {
        console.warn('[LiveTracking] VITE_MAPBOX_API_KEY not set — skipping route fetch');
        return [];
      }
      const url = `https://api.mapbox.com/directions/v5/mapbox/driving/${startCoords.lng},${startCoords.lat};${destinationCoords.lng},${destinationCoords.lat}?geometries=geojson&overview=full&access_token=${mapboxDirectionsToken}`;
      const response = await fetch(url);
      const data = await response.json();

      if (data.routes && data.routes[0]) {
        // Extract coordinates from the route geometry
        const coordinates = data.routes[0].geometry.coordinates;

        // Calculate cumulative distance along the route
        const distances: number[] = [0];
        let totalDistance = 0;

        for (let i = 1; i < coordinates.length; i++) {
          const lat1 = coordinates[i - 1][1];
          const lng1 = coordinates[i - 1][0];
          const lat2 = coordinates[i][1];
          const lng2 = coordinates[i][0];

          // Simple Euclidean distance (good enough for small distances)
          const segmentDist = Math.sqrt(
            Math.pow(lat2 - lat1, 2) + Math.pow(lng2 - lng1, 2)
          );
          totalDistance += segmentDist;
          distances.push(totalDistance);
        }

        // Resample route for demo (faster journey for client presentation)
        const targetPointCount = 250; // ~2.8 minutes at demo speed (0.30 * 10 = 3 points/sec)
        const sampledPoints: Array<{ lat: number; lng: number }> = [];

        for (let i = 0; i < targetPointCount; i++) {
          // Calculate target distance along the route
          const targetDistance = (i / (targetPointCount - 1)) * totalDistance;

          // Find the segment that contains this distance
          let segmentIndex = 0;
          for (let j = 1; j < distances.length; j++) {
            if (distances[j] >= targetDistance) {
              segmentIndex = j - 1;
              break;
            }
          }

          // Calculate fraction within the segment
          const segmentStart = distances[segmentIndex];
          const segmentEnd = distances[segmentIndex + 1] || totalDistance;
          const segmentLength = segmentEnd - segmentStart;
          const fraction = segmentLength > 0 ? (targetDistance - segmentStart) / segmentLength : 0;

          // Interpolate between the two points
          const lower = { lng: coordinates[segmentIndex][0], lat: coordinates[segmentIndex][1] };
          const upper = {
            lng: coordinates[Math.min(segmentIndex + 1, coordinates.length - 1)][0],
            lat: coordinates[Math.min(segmentIndex + 1, coordinates.length - 1)][1]
          };

          sampledPoints.push({
            lat: lower.lat + (upper.lat - lower.lat) * fraction,
            lng: lower.lng + (upper.lng - lower.lng) * fraction,
          });
        }

        setRoutePoints(sampledPoints);
        console.log(`Loaded ${sampledPoints.length} route points evenly distributed by distance (from ${coordinates.length} original points, total distance: ${totalDistance.toFixed(4)})`);
        return sampledPoints;
      }
    } catch (error) {
      console.error('Failed to fetch route:', error);
    }
    return [];
  };

  // Listen for debug mode from localStorage
  useEffect(() => {
    const checkDebugMode = () => {
      const stored = localStorage.getItem('debugMode');
      setDebugMode(stored === 'true');
    };

    // Check initially
    checkDebugMode();

    // Listen for storage changes
    window.addEventListener('storage', checkDebugMode);

    // Poll for changes (in case it's changed in same window)
    const interval = setInterval(checkDebugMode, 500);

    return () => {
      window.removeEventListener('storage', checkDebugMode);
      clearInterval(interval);
    };
  }, []);

  // Initial load
  useEffect(() => {
    const loadTracking = async () => {
      setLoading(true);
      try {
        await new Promise(resolve => setTimeout(resolve, 500));
        // Fetch the route first
        const points = await fetchRoute();

        // Initialize tracking with the first point of the actual route
        const initialTracking = getMockTracking(orderId || '');
        if (points.length > 0) {
          initialTracking.driverLocation = {
            ...initialTracking.driverLocation,
            lat: points[0].lat,
            lng: points[0].lng,
          };

          // Calculate initial ETA based on route length and speed
          const progressIncrement = driverSpeed * 10;
          const pointsPerSecond = progressIncrement / refreshInterval;
          const totalSeconds = points.length / pointsPerSecond;
          initialTracking.etaMinutes = Math.round(totalSeconds / 60);

          console.log(`Initial ETA calculation: ${points.length} points, ${pointsPerSecond.toFixed(2)} points/sec, ${initialTracking.etaMinutes} minutes`);
        }
        setTracking(initialTracking);
      } finally {
        setLoading(false);
      }
    };
    loadTracking();
  }, [orderId]);

  // Poll for updates (simulated)
  useEffect(() => {
    if (!tracking) return;

    const interval = setInterval(() => {
      simulateDriverMovement();
    }, refreshInterval * 1000); // Update based on refresh interval

    return () => clearInterval(interval);
  }, [tracking, simulateDriverMovement, refreshInterval]);

  // Fit the viewport to cover the driver + destination once both are
  // available. Runs once per page load (fittedBoundsRef guards against
  // re-fitting after the driver starts moving — that's what flyTo handles).
  useEffect(() => {
    if (!mapReady || !tracking?.driverLocation || fittedBoundsRef.current) return;
    const handle = mapHandleRef.current;
    if (!handle) return;
    handle.fitBounds(
      [
        { lat: tracking.driverLocation.lat, lng: tracking.driverLocation.lng },
        { lat: destinationCoords.lat, lng: destinationCoords.lng },
      ],
      { padding: 100, maxZoom: 18, duration: 600 },
    );
    fittedBoundsRef.current = true;
  }, [mapReady, tracking?.driverLocation]);

  // NOTE: Debug-mode visualizations dropped during the Phase 4 (#325)
  // migration off `mapbox-gl`:
  //   - Numbered debug point markers along the route
  //   - Geofence circle overlays
  // Both relied on Mapbox-specific GeoJSON `circle` / `fill` / `line` /
  // `symbol` layers. The MapConnector interface doesn't currently expose
  // those primitives — re-adding them needs a `Circle` (and likely
  // `LayerGroup`) capability on both adapters. Geofence *detection* JS
  // (driver-in-circle haversine check below) is preserved — the visual
  // overlay is the only thing missing.
  useEffect(() => {
    console.log('[LiveTracking] active maps provider:', ACTIVE_PROVIDER);
  }, []);

  // Check if driver is in any geofences
  useEffect(() => {
    if (!tracking?.driverLocation) return;

    const checkGeofences = () => {
      const driverPoint = [tracking.driverLocation.lng, tracking.driverLocation.lat];
      const insideGeofences: string[] = [];

      geofences.forEach(geofence => {
        let inside = false;

        if (geofence.type === 'circle') {
          // Distance-based check for circles
          const [centerLng, centerLat] = geofence.center;
          const [driverLng, driverLat] = driverPoint;

          // Calculate distance using Haversine formula (approximate for short distances)
          const R = 6371000; // Earth radius in meters
          const dLat = (driverLat - centerLat) * Math.PI / 180;
          const dLng = (driverLng - centerLng) * Math.PI / 180;
          const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
                    Math.cos(centerLat * Math.PI / 180) * Math.cos(driverLat * Math.PI / 180) *
                    Math.sin(dLng / 2) * Math.sin(dLng / 2);
          const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
          const distance = R * c;

          inside = distance <= geofence.radius;
        }
        // No else needed - we only have circles for now
        // If we add polygon geofences later, we'll handle them here

        if (inside) {
          insideGeofences.push(geofence.id);
        }
      });

      // Update active geofences if changed
      if (JSON.stringify(insideGeofences) !== JSON.stringify(activeGeofences)) {
        setActiveGeofences(insideGeofences);

        // Log geofence events
        insideGeofences.forEach(id => {
          if (!activeGeofences.includes(id)) {
            const geofence = geofences.find(g => g.id === id);
            console.log(`🚨 Driver entered geofence: ${geofence?.name}`);
          }
        });

        activeGeofences.forEach(id => {
          if (!insideGeofences.includes(id)) {
            const geofence = geofences.find(g => g.id === id);
            console.log(`✅ Driver exited geofence: ${geofence?.name}`);
          }
        });
      }
    };

    checkGeofences();
  }, [tracking?.driverLocation, activeGeofences]);

  // Driver marker position + visibility is now declarative — see the
  // conditional <Marker /> render below. No imperative DOM patching needed.

  const formatETA = (minutes: number) => {
    if (minutes < 1) return 'Arriving now';
    if (minutes === 1) return '1 minute';
    return `${minutes} minutes`;
  };

  const getArrivalTime = (minutes: number) => {
    const arrival = new Date();
    arrival.setMinutes(arrival.getMinutes() + minutes);
    return arrival.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
    });
  };

  if (loading) {
    return (
      <div className={styles.loading}>
        <Spinner size="large" label="Loading tracking..." />
      </div>
    );
  }

  if (!tracking) {
    return <div>Tracking not available</div>;
  }

  // Check if ETA is 5 minutes or less to show live tracking
  const showLiveTracking = tracking.etaMinutes !== undefined && tracking.etaMinutes <= 5;

  return (
    <div className={styles.container}>
      {/* Header */}
      <div className={styles.header}>
        <Text className={styles.headerTitle}>Tracking Order #{orderId?.slice(-4)}</Text>
        <div className={styles.headerActions}>
          <Button appearance="subtle" icon={<Share24Regular />}>
            Share
          </Button>
          <Button
            appearance="subtle"
            icon={<Dismiss24Regular />}
            onClick={() => navigate(`/portal/orders/${orderId}`)}
          />
        </div>
      </div>

      {/* Map - always render */}
      <div className={styles.mapContainer}>
        <MapView
          center={destinationCoords}
          zoom={18}
          style="streets"
          onLoad={() => setMapReady(true)}
          onMoveOrZoom={() => {
            // Track current zoom for the debug overlay readout. MapHandle
            // doesn't expose getZoom() so we don't fight Google's API
            // surface here — debug zoom display will read its initial
            // value only. Adequate; this is a debug-only field.
          }}
          mapRef={(h) => { mapHandleRef.current = h; }}
          className={styles.map}
          style_={{ width: '100%', height: '100%' }}
        >
          {/* Destination marker (blue). */}
          <Marker position={destinationCoords} color="#0078d4" />

          {/* Route polyline (renders straight-line segments through the
              sampled route points; visual aid only — Routes API for
              turn-by-turn is a future ticket). */}
          {routePoints.length > 1 && (
            <Polyline points={routePoints} color="#0078d4" width={4} opacity={0.75} />
          )}

          {/* Driver marker — custom truck icon. Only rendered when the
              live-tracking window is open (ETA ≤ 5 min) so the customer
              sees the driver appear at handoff time, matching the
              historical display:none behavior. */}
          {showLiveTracking && tracking.driverLocation && (
            <Marker
              position={{ lat: tracking.driverLocation.lat, lng: tracking.driverLocation.lng }}
              iconElement={
                <div
                  style={{
                    width: 40,
                    height: 40,
                    background: '#ff8c00',
                    borderRadius: '50%',
                    border: '3px solid white',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: 20,
                  }}
                >
                  🚛
                </div>
              }
            />
          )}
        </MapView>

        {/* Overlay waiting screen if ETA > 5 minutes */}
        {!showLiveTracking && (
          <div className={styles.waitingOverlay}>
            <div className={styles.waitingContent}>
              <Clock24Regular style={{ fontSize: '64px', color: tokens.colorNeutralForeground1, marginBottom: '16px' }} />
              <Text className={styles.waitingTitle}>Driver En Route</Text>
              <Text className={styles.waitingSubtitle}>
                Your driver {tracking.driver?.name} is on the way
              </Text>
              <div className={styles.etaCard}>
                <Text className={styles.etaCardLabel}>Estimated Arrival</Text>
                <Text className={styles.etaCardValue}>{getArrivalTime(tracking.etaMinutes || 0)}</Text>
                <Text className={styles.etaCardTime}>In {formatETA(tracking.etaMinutes || 0)}</Text>
              </div>
              <Text className={styles.waitingNote}>
                Live tracking will be available when your driver is 5 minutes away
              </Text>
            </div>
          </div>
        )}

        {/* Refresh Control */}
        <div className={styles.refreshControl}>
          <Text className={styles.refreshLabel}>Refresh Interval</Text>
          <Slider
            className={styles.refreshSlider}
            min={1}
            max={10}
            value={refreshInterval}
            onChange={(e, data) => setRefreshInterval(data.value)}
          />
          <Text className={styles.refreshValue}>{refreshInterval} seconds</Text>

          <div className={styles.controlDivider} />

          <Text className={styles.refreshLabel}>Driver Speed</Text>
          <Slider
            className={styles.refreshSlider}
            min={0.05}
            max={0.30}
            step={0.05}
            value={driverSpeed}
            onChange={(e, data) => setDriverSpeed(data.value)}
          />
          <Text className={styles.refreshValue}>
            {driverSpeed === 0.05 ? 'Slow' : driverSpeed === 0.15 ? 'Normal' : driverSpeed === 0.30 ? 'Fast' : driverSpeed.toFixed(2)}
          </Text>
        </div>

        {/* Debug Overlay */}
        {debugMode && (
          <div className={styles.debugOverlay}>
            <div className={styles.debugTitle}>🔧 DEBUG INFO</div>
            <div className={styles.debugRow}>
              <span className={styles.debugLabel}>Map Zoom:</span>
              <span className={styles.debugValue}>{mapZoom.toFixed(2)}</span>
            </div>
            <div className={styles.debugRow}>
              <span className={styles.debugLabel}>Driver Speed:</span>
              <span className={styles.debugValue}>{driverSpeed.toFixed(2)}</span>
            </div>
            <div className={styles.debugRow}>
              <span className={styles.debugLabel}>Refresh Interval:</span>
              <span className={styles.debugValue}>{refreshInterval}s</span>
            </div>
            <div className={styles.debugRow}>
              <span className={styles.debugLabel}>Route Points:</span>
              <span className={styles.debugValue}>{routePoints.length}</span>
            </div>
            <div className={styles.debugRow}>
              <span className={styles.debugLabel}>Current Point:</span>
              <span className={styles.debugValue}>{currentRouteIndexRef.current}</span>
            </div>
            <div className={styles.debugRow}>
              <span className={styles.debugLabel}>Progress:</span>
              <span className={styles.debugValue}>{routeProgressRef.current.toFixed(2)}</span>
            </div>
            <div className={styles.debugRow}>
              <span className={styles.debugLabel}>ETA:</span>
              <span className={styles.debugValue}>{tracking?.etaMinutes || 0} min</span>
            </div>
            <div className={styles.debugRow}>
              <span className={styles.debugLabel}>Status:</span>
              <span className={styles.debugValue}>{tracking?.status || 'N/A'}</span>
            </div>
            <div className={styles.debugRow}>
              <span className={styles.debugLabel}>Driver Lat:</span>
              <span className={styles.debugValue}>{tracking?.driverLocation?.lat.toFixed(5) || 'N/A'}</span>
            </div>
            <div className={styles.debugRow}>
              <span className={styles.debugLabel}>Driver Lng:</span>
              <span className={styles.debugValue}>{tracking?.driverLocation?.lng.toFixed(5) || 'N/A'}</span>
            </div>
            <div className={styles.debugRow}>
              <span className={styles.debugLabel}>Geofences:</span>
              <span className={styles.debugValue}>
                {activeGeofences.length > 0
                  ? activeGeofences.map(id => geofences.find(g => g.id === id)?.name).join(', ')
                  : 'None'}
              </span>
            </div>
          </div>
        )}

        {/* Info Panel */}
        <div className={styles.infoPanel}>
          {/* ETA Section */}
          <div className={styles.etaSection}>
            <div className={styles.etaHeader}>
              <div className={styles.etaIcon}>
                <VehicleCab24Regular />
              </div>
              <div>
                <Text className={styles.etaTitle}>
                  {tracking.driver?.name} is on the way
                </Text>
              </div>
            </div>
            <Text className={styles.etaValue}>
              {formatETA(tracking.etaMinutes || 0)}
            </Text>
            <Text className={styles.etaSubtitle}>
              Estimated arrival: {getArrivalTime(tracking.etaMinutes || 0)}
            </Text>
          </div>

          {/* Driver Section */}
          <div className={styles.driverSection}>
            <Avatar name={tracking.driver?.name} size={48} color="colorful" />
            <div className={styles.driverInfo}>
              <Text className={styles.driverName}>{tracking.driver?.name}</Text>
              <Text className={styles.driverVehicle}>
                {tracking.driver?.truckName} • (787) 555-1234
              </Text>
            </div>
            <div className={styles.driverActions}>
              <Button
                appearance="subtle"
                icon={<Call24Regular />}
                onClick={() => window.open('tel:+17875551234')}
              />
              <Button
                appearance="subtle"
                icon={<Chat24Regular />}
                onClick={() => navigate('/portal/messages')}
              />
            </div>
          </div>

          {/* Destination Section */}
          <div className={styles.destinationSection}>
            <Text className={styles.destinationLabel}>Delivering to</Text>
            <div className={styles.destinationAddress}>
              <Location24Regular style={{ color: tokens.colorBrandForeground1, flexShrink: 0 }} />
              <Text>{destinationAddress}</Text>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LiveTracking;
